import { useEffect, useRef } from 'react';

const subDirections = [
  { name: '原創愛情', description: '以原創故事與人物情感為核心' },
  { name: '古代言情', description: '以古代時空為背景的情感敘事' },
  { name: '情感故事', description: '聚焦關係中的心動、選擇與轉折' },
  { name: '人物成長', description: '角色在愛情與人生中的自我理解' },
];

export default function PublishingDirectionsSection() {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (mediaQuery.matches) {
      el.querySelectorAll('[data-animate]').forEach((child) => {
        (child as HTMLElement).classList.remove('opacity-0', 'translate-y-6');
      });
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const children = entry.target.querySelectorAll('[data-animate]');
            children.forEach((child, i) => {
              setTimeout(() => {
                (child as HTMLElement).classList.remove('opacity-0', 'translate-y-6');
              }, 100 + i * 80);
            });
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <section className="min-h-screen-dynamic flex items-center bg-background-50">
      <div ref={ref} className="section-container w-full py-16 md:py-0">
        <div className="text-center mb-10 max-w-[800px] mx-auto">
          <span data-animate className="opacity-0 translate-y-6 transition-all duration-700 inline-block px-3 py-1 text-xs font-label bg-secondary-50 text-secondary-700 rounded-full mb-3">
            出版方向
          </span>
          <h2 data-animate className="opacity-0 translate-y-6 transition-all duration-700 text-2xl md:text-3xl font-heading font-semibold text-foreground-900 leading-tight mb-4">
            主要出版方向
          </h2>
          <p data-animate className="opacity-0 translate-y-6 transition-all duration-700 text-sm md:text-base text-foreground-600 leading-relaxed max-w-[720px] mx-auto">
            未境原創聚焦愛情小說出版，透過不同人物、情感關係與故事情境，提供多元的小說閱讀內容。
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-center max-w-[1000px] mx-auto">
          <div className="order-2 lg:order-1 space-y-6">
            <div data-animate className="opacity-0 translate-y-6 transition-all duration-700">
              <div className="rounded-xl bg-background-100 border border-background-200/70 p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-50">
                    <i className="ri-heart-line text-accent-500 text-lg"></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-heading font-semibold text-foreground-900">
                      愛情小說
                    </h3>
                    <p className="text-xs text-foreground-400">
                      主要出版方向
                    </p>
                  </div>
                </div>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  出版以愛情、情感關係與人物故事為核心的小說作品，透過不同角色與情境，呈現多元的情感敘事與文學閱讀內容。
                </p>
              </div>
            </div>

            <div data-animate className="opacity-0 translate-y-6 transition-all duration-700">
              <h4 className="text-xs font-label font-medium text-foreground-400 mb-3">
                作品涵蓋方向
              </h4>
              <div className="space-y-2.5">
                {subDirections.map((dir) => (
                  <div
                    key={dir.name}
                    className="flex items-center gap-3 px-4 py-3 rounded-lg bg-background-100 border border-background-200/70"
                  >
                    <div className="w-8 h-8 flex items-center justify-center rounded-full bg-secondary-50 flex-shrink-0">
                      <i className="ri-book-open-line text-secondary-500 text-sm"></i>
                    </div>
                    <div>
                      <p className="text-sm font-label font-medium text-foreground-800">
                        {dir.name}
                      </p>
                      <p className="text-xs text-foreground-400">
                        {dir.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div data-animate className="opacity-0 translate-y-6 transition-all duration-700">
              <div className="rounded-lg bg-background-100 border border-background-200/70 px-4 py-3.5 flex items-start gap-3">
                <div className="w-6 h-6 flex items-center justify-center rounded-full bg-background-200/70 flex-shrink-0 mt-0.5">
                  <i className="ri-information-line text-foreground-400 text-xs"></i>
                </div>
                <p className="text-xs text-foreground-500 leading-relaxed">
                  愛情小說屬於文學創作與出版閱讀內容，不代表未境原創提供感情諮詢、婚姻諮商、戀愛配對或其他個人關係服務。
                </p>
              </div>
            </div>
          </div>

          <div data-animate className="opacity-0 translate-y-6 transition-all duration-700 order-1 lg:order-2">
            <div className="relative aspect-[4/3] rounded-xl overflow-hidden">
              <img
                src="https://readdy.ai/api/search-image?query=Soft%20natural%20light%20falling%20across%20an%20open%20book%20with%20handwritten%20notes%20and%20a%20delicate%20dried%20flower%20pressed%20between%20pages%2C%20cream%20linen%20fabric%20texture%20beneath%2C%20gentle%20warmth%20from%20nearby%20window%2C%20minimalist%20literary%20aesthetic%20with%20wabi%20sabi%20sensibility%2C%20quiet%20contemplative%20atmosphere%2C%20muted%20terracotta%20and%20warm%20beige%20tones%2C%20editorial%20still%20life%20photography%20with%20organic%20composition%20and%20poetic%20negative%20space&width=800&height=600&seq=publishing-dir-2026&orientation=landscape"
                alt="柔和光線下的書本與壓花，文學出版意象"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}